import { Link } from "react-router-dom";
import { ShieldCheck, EyeOff, MapPin, Mail, User, Phone, Lock, ArrowLeft } from "lucide-react";

/**
 * PrivacyDisclaimer — plain-English privacy commitment page.
 *
 * Not a full legal privacy policy (that lives at /privacy-policy). This page
 * exists to make one specific promise unambiguous to customers: the platform
 * masks or withholds every piece of raw PII from the businesses that
 * subscribe. Businesses never see exact street numbers, full names, emails,
 * or phone numbers — even at the Pro subscription tier.
 */
const HIDDEN_ITEMS = [
  {
    icon: MapPin,
    label: "Exact street address",
    detail: "Building & unit numbers are stripped server-side (`strip_street_number`). Businesses only see the street name, city, and state — e.g., \"Elm Street, Oakland, CA\", never \"742 Elm Street, Apt 3B\".",
  },
  {
    icon: User,
    label: "Full name",
    detail: "Customer names are masked to first-initial + last-name (e.g., \"S. Pine\") wherever a business can see them: directory, reviews, loyalty rosters, customer detail pages.",
  },
  {
    icon: Mail,
    label: "Email address",
    detail: "Businesses can send you promotional messages only through the Servrevu send pipeline. Your address is never exposed to them — even in replies. Every marketing message includes an unsubscribe link that mutes that specific business instantly.",
  },
  {
    icon: Phone,
    label: "Phone number",
    detail: "Phone numbers are optional at signup and are never displayed to any business. We do not sell, share, or SMS-forward your number.",
  },
];

const SHOWN_ITEMS = [
  {
    label: "First initial + last name",
    example: '"S. Pine"',
  },
  {
    label: "Street name (no number)",
    example: '"Elm Street"',
  },
  {
    label: "City & state",
    example: '"Oakland, CA"',
  },
  {
    label: "Overall customer star rating",
    example: '"4.7 · 12 reviews"',
  },
];

export default function PrivacyDisclaimer() {
  return (
    <div className="max-w-4xl mx-auto px-6 md:px-10 py-14">
      <Link
        to="/"
        className="inline-flex items-center gap-1.5 text-xs font-mono text-gray-500 hover:text-[#B31E1E] mb-6"
        data-testid="privacy-back-link"
      >
        <ArrowLeft size={13} /> Back to Servrevu
      </Link>

      <div className="flex items-start gap-4">
        <div className="h-14 w-14 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
          <ShieldCheck size={26} />
        </div>
        <div className="min-w-0">
          <div className="overline text-emerald-700">Customer privacy commitment</div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tighter mt-2 leading-[1.05]">
            Your personal information is <em className="not-italic text-emerald-700">not</em> shared with the businesses on Servrevu.
          </h1>
          <p className="mt-4 text-gray-600 max-w-2xl leading-relaxed">
            Servrevu is a two-way marketing ledger — businesses subscribe to
            reach customers with promotions and record loyalty. That subscription
            gives them <strong>zero access to your raw personal data</strong>. This
            page spells out exactly what they can and cannot see.
          </p>
        </div>
      </div>

      <section className="mt-12" data-testid="privacy-hidden-section">
        <div className="flex items-center gap-2">
          <EyeOff size={18} className="text-rose-600" />
          <h2 className="font-display text-2xl">Never shared with businesses</h2>
        </div>
        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          {HIDDEN_ITEMS.map((it) => (
            <div
              key={it.label}
              className="rounded-md border border-rose-200 bg-rose-50/50 p-4"
              data-testid={`privacy-hidden-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="flex items-center gap-2">
                <it.icon size={16} className="text-rose-600" />
                <h3 className="font-medium text-gray-900">{it.label}</h3>
              </div>
              <p className="mt-2 text-sm text-gray-700 leading-relaxed">{it.detail}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-12" data-testid="privacy-shown-section">
        <div className="flex items-center gap-2">
          <ShieldCheck size={18} className="text-emerald-600" />
          <h2 className="font-display text-2xl">What businesses do see</h2>
        </div>
        <p className="mt-2 text-sm text-gray-600 max-w-2xl">
          Just enough to leave a fair review and target relevant offers — nothing that could identify you off-platform.
        </p>
        <div className="mt-5 border border-border rounded-md divide-y divide-border bg-white">
          {SHOWN_ITEMS.map((it) => (
            <div key={it.label} className="flex items-center justify-between px-4 py-3 text-sm">
              <span className="text-gray-800">{it.label}</span>
              <span className="font-mono text-gray-500">{it.example}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-12" data-testid="privacy-how-messaging">
        <div className="flex items-center gap-2">
          <Mail size={18} className="text-[#B31E1E]" />
          <h2 className="font-display text-2xl">How a business can contact you</h2>
        </div>
        <p className="mt-3 text-gray-700 leading-relaxed max-w-2xl">
          Every message from a business flows through Servrevu:
        </p>
        <ul className="mt-3 space-y-2 text-sm text-gray-700">
          <li className="flex gap-2"><span className="text-[#B31E1E]">·</span> A business can message you only if you have not muted them AND you have their category selected in your promotional preferences.</li>
          <li className="flex gap-2"><span className="text-[#B31E1E]">·</span> Every promotional message includes a one-click unsubscribe link specific to that business — muting takes effect immediately.</li>
          <li className="flex gap-2"><span className="text-[#B31E1E]">·</span> A business owner responding to your review sends the reply through our pipeline. They never see your real inbox address.</li>
          <li className="flex gap-2"><span className="text-[#B31E1E]">·</span> The master "Receive promotional messages" toggle on your dashboard pauses <em>every</em> outbound message, regardless of category settings.</li>
        </ul>
      </section>

      <section className="mt-12" data-testid="privacy-loyalty">
        <div className="flex items-center gap-2">
          <MapPin size={18} className="text-[#B31E1E]" />
          <h2 className="font-display text-2xl">Loyalty check-ins &amp; GPS</h2>
        </div>
        <p className="mt-3 text-gray-700 leading-relaxed max-w-2xl">
          When you check in for a loyalty stamp, we verify you're physically at the business by comparing your device's coarse location to their address radius. Your <strong>precise coordinates are not stored</strong> on the business record — only the derived visit count and whether it passed. Photos you upload with a check-in are visible on the business profile (masked to your initial + last name) unless the business owner has opted out of the public feed.
        </p>
      </section>

      <section className="mt-12" data-testid="privacy-your-rights">
        <div className="flex items-center gap-2">
          <Lock size={18} className="text-[#B31E1E]" />
          <h2 className="font-display text-2xl">Your rights</h2>
        </div>
        <ul className="mt-3 space-y-2 text-sm text-gray-700">
          <li className="flex gap-2"><span className="text-[#B31E1E]">·</span> View and edit your address, name, and email at any time on your dashboard.</li>
          <li className="flex gap-2"><span className="text-[#B31E1E]">·</span> Delete your account permanently — all reviews and check-ins are removed.</li>
          <li className="flex gap-2"><span className="text-[#B31E1E]">·</span> Request an export of everything we hold about you: <a href="mailto:support@servrevu.com" className="text-[#B31E1E] hover:underline">support@servrevu.com</a>.</li>
          <li className="flex gap-2"><span className="text-[#B31E1E]">·</span> Dispute any business-authored review of you — disputes are public and visible on the review.</li>
        </ul>
      </section>

      <section className="mt-12 rounded-lg border border-emerald-200 bg-emerald-50/50 p-5" data-testid="privacy-summary">
        <div className="flex items-start gap-3">
          <ShieldCheck size={22} className="text-emerald-700 shrink-0 mt-0.5" />
          <div>
            <h3 className="font-display text-xl text-emerald-900">Bottom line</h3>
            <p className="mt-1 text-sm text-emerald-900/90 leading-relaxed max-w-2xl">
              A business paying for a Servrevu Pro subscription can rate you, send you messages you've opted into, and see a masked profile. They cannot message you outside the platform, cannot call you, cannot show up at your door, and cannot resell any part of your information. That's the deal, in writing.
            </p>
          </div>
        </div>
      </section>

      <p className="mt-10 text-xs font-mono text-gray-400">
        Last updated {new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })} · Questions? <a href="mailto:support@servrevu.com" className="text-[#B31E1E] hover:underline">support@servrevu.com</a>
      </p>
    </div>
  );
}
